import { Context, Effect, ParseResult, Schema as S } from "effect";
import { Country } from "./countries";
import { Language } from "./language";
class Bcp47Parser extends Context.Tag("@open-revenue/schema/Bcp47Parser")() {
}
/**
 * All the postfixes I've seen after the '@' in locales
 *
 * calendar
 * rg
 * numbers
 */ //
// Numbers
//
const LocaleNumbersSchema = S.String.pipe(S.brand("@open-revenue/schema/Locale/Numbers"), S.annotations({
    description: "Numbers",
    examples: [
        "khmr",
        "latn",
        "arab"
    ]
}));
const LocaleNumbersValues = [
    "latn",
    "khmr",
    "arab"
];
const LocaleNumbersStrict = S.Union(...LocaleNumbersValues.map((value)=>S.Literal(value))).pipe(S.brand("@open-revenue/schema/Locale/Numbers/Strict"), S.annotations({
    description: "Numbers",
    examples: LocaleNumbersValues
}));
// Eeither the
const LocaleNumbersStrictFromLocaleNumbersOrNull = S.transformOrFail(LocaleNumbersSchema.pipe(S.encodedSchema, S.NullOr), LocaleNumbersStrict.pipe(S.NullOr), {
    encode: (value)=>Effect.succeed(value),
    decode: (value)=>Effect.gen(function*() {
            if (value === null || !LocaleNumbersValues.includes(value)) {
                return null;
            }
            return value;
        }),
    strict: true
});
const LocaleNumbers = {
    Type: LocaleNumbersSchema.Type,
    Schema: LocaleNumbersSchema,
    Strict: {
        Schema: LocaleNumbersStrict,
        FromStringOrNull: LocaleNumbersStrictFromLocaleNumbersOrNull,
        Type: LocaleNumbersStrict.Type,
        make: (value)=>S.decodeUnknown(LocaleNumbersStrict)(value),
        makeUnsafe: (value)=>value
    },
    StrictFromLocaleNumbersOrNull: LocaleNumbersStrictFromLocaleNumbersOrNull,
    make: (value)=>S.decode(LocaleNumbersSchema)(value),
    makeUnsafe: (value)=>value
};
//
// Calendar
//
const LocaleCalendarSchema = S.String.pipe(S.brand("@open-revenue/schema/Locale/Calendar"), S.annotations({
    description: "Calendar",
    examples: [
        "buddhist",
        "gregorian",
        "unknown"
    ]
}));
const LocaleCalendarValues = [
    "buddhist",
    "gregorian",
    "japanese"
];
const LocaleCalendarStrict = S.Union(...LocaleCalendarValues.map((value)=>S.Literal(value))).pipe(S.brand("@open-revenue/schema/Locale/Calendar/Strict"));
const LocaleCalendarStrictFromLocaleCalendarOrNull = S.transformOrFail(LocaleCalendarSchema.pipe(S.encodedSchema, S.NullOr), LocaleCalendarStrict.pipe(S.NullOr), {
    encode: (value)=>Effect.succeed(value),
    decode: (value)=>Effect.gen(function*() {
            if (value === null || !LocaleCalendarValues.includes(value)) {
                return null;
            }
            return value;
        })
});
const LocaleCalendar = {
    Type: LocaleCalendarSchema.Type,
    Schema: {
        LocaleCalendarSchema,
        Type: LocaleCalendarSchema.Type,
        make: (value)=>S.decode(LocaleCalendarSchema)(value),
        makeUnsafe: (value)=>value
    },
    Strict: {
        Schema: LocaleCalendarStrict,
        FromStringOrNull: LocaleCalendarStrictFromLocaleCalendarOrNull,
        Type: LocaleCalendarStrict.Type,
        make: (value)=>S.decodeUnknown(LocaleCalendarStrict)(value),
        makeUnsafe: (value)=>value
    }
};
//
// Region
//
const LocaleRegionStringSchema = S.String.pipe(S.brand("@open-revenue/schema/Locale/Region/String"), S.annotations({
    description: "Region, can be weird ones like 'zhzzzzz'"
}));
//
// Locale Parts (Language + Region)
//
const LocaleLanguage = S.Struct({
    //
    // Language
    //
    // "en", "ru",  etc ("en_US" -> "en")
    language: Language.ISO6391.AnyStep.Schema,
    // "US", "FR", etc ("en_US" -> "US")
    languageRegion: Country.Code.Alpha2.Schema.pipe(S.NullOr),
    // Script "zh-Hant-US" -> "Hant"
    languageScript: Language.Script.ISO15924.Schema.pipe(S.NullOr)
});
const LocaleLanguageNullable = S.Struct({
    language: Language.ISO6391.AnyStep.Schema.pipe(S.NullOr),
    languageRegion: Country.Code.Alpha2.Schema.pipe(S.NullOr),
    languageScript: Language.Script.ISO15924.Schema.pipe(S.NullOr)
});
const LocaleRegion = S.Struct({
    //
    // Locale
    //
    // localeRegion "en_US@rg=zhzzzzz" -> "zhzzzzz"
    localeRegion: LocaleRegionStringSchema,
    // localeNumbers "en_US@numbers=latn" -> "latn"
    localeNumbers: LocaleNumbers.Strict.Schema,
    // localeCalendar "en_US@calendar=buddhist" -> "buddhist"
    localeCalendar: LocaleCalendar.Strict.Schema
});
const LocaleRegionNullable = S.Struct({
    localeRegion: LocaleRegionStringSchema.pipe(S.NullOr),
    localeNumbers: LocaleNumbers.Strict.FromStringOrNull,
    localeCalendar: LocaleCalendar.Strict.FromStringOrNull
});
const ParsedLocaleSchema = S.Struct({
    language: LocaleLanguage,
    region: LocaleRegion.pipe(S.NullOr)
});
//
// Parsing
//
const LocalePartsFromString = S.transform(S.String, S.Struct({
    language: S.String,
    locale: S.String.pipe(S.NullOr)
}), {
    decode: (value)=>{
        const [language, locale] = value.split("@");
        return {
            language,
            locale
        };
    },
    encode: (value)=>{
        return `${value.language}${value.locale ? `@${value.locale}` : ""}`;
    }
});
const LocaleLanguageFromLocaleLanguageOrNull = S.transformOrFail(S.String, LocaleLanguageNullable, {
    decode: (value)=>Effect.gen(function*() {
            const bcp47Parser = yield* Bcp47Parser;
            const result = bcp47Parser(value.replace("_", "-"), {
                normalize: true,
                forgiving: true
            });
            return {
                language: result.language || null,
                languageRegion: result.region || null,
                languageScript: result.script || null
            };
        }),
    encode: (value, _, ast)=>Effect.fail(new ParseResult.Forbidden(ast, "", "Not allowed to encode")),
    strict: true
});
const LocaleRegionFromLocaleRegionOrNull = S.transformOrFail(S.String.pipe(S.NullOr), LocaleRegionNullable.pipe(S.NullOr, S.encodedSchema), {
    decode: (value)=>Effect.gen(function*() {
            if (value === null) {
                return null;
            }
            const localeRegionMap = value.split(";").reduce((acc, pair)=>{
                const [key, value] = pair.split("=");
                return {
                    ...acc,
                    [key]: value
                };
            }, {});
            const localeRegion = localeRegionMap["rg"] || null;
            const localeNumbers = localeRegionMap["numbers"] || null;
            const localeCalendar = localeRegionMap["calendar"] || null;
            return {
                localeRegion,
                localeNumbers,
                localeCalendar
            };
        }),
    encode: (value, _, ast)=>Effect.fail(new ParseResult.Forbidden(ast, "", "Not allowed to encode")),
    strict: true
});
// Encoded of first is original encoded
// Type of first is encoded of second
const ParsedLocaleNullableFromString = S.compose(LocalePartsFromString, S.Struct({
    language: LocaleLanguageFromLocaleLanguageOrNull,
    locale: LocaleRegionFromLocaleRegionOrNull
}));
export const Locale = {
    Parsed: ParsedLocaleSchema,
    ParsedNullableFromString: ParsedLocaleNullableFromString,
    Language: LocaleLanguage,
    Region: LocaleRegion,
    Numbers: LocaleNumbers,
    Calendar: LocaleCalendar
};
